import React from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { useAuth } from '../../shared/contexts/auth-context';
import { isMasterUser } from '../../shared/utils/permissions';

// GraphQL query to fetch search history
const GET_SEARCH_HISTORY = gql`
  query SearchHistory {
    searchHistory {
      id
      fullName
      idNumber
      entityName
      tfsMatch
      googleResults
      googleError
      timestamp
    }
  }
`;

// GraphQL query to fetch current user
const GET_CURRENT_USER = gql`
  query Me {
    me {
      id
      username
      role
    }
  }
`;

// GraphQL mutation to delete a search
const DELETE_SEARCH = gql`
  mutation DeleteSearch($id: ID!) {
    deleteSearch(id: $id)
  }
`;

const SearchHistory = ({ onDownloadPDF, userRole: propUserRole }) => {
  const { loading, error, data, refetch } = useQuery(GET_SEARCH_HISTORY);
  const { data: userData } = useQuery(GET_CURRENT_USER);
  const auth = useAuth();

  const [deleteSearch, { loading: deleteLoading, error: deleteError }] = useMutation(DELETE_SEARCH, {
    onCompleted: () => {
      refetch(); // Refresh the search history list after deletion
    },
    onError: (err) => {
      console.error('Delete search error:', err.message);
    },
  });

  // Handle PDF download by using the passed prop
  const handleDownloadPDF = (id) => {
    if (onDownloadPDF) {
      onDownloadPDF(id);
    } else {
      console.error('onDownloadPDF function not provided');
    }
  };

  // Handle search deletion
  const handleDeleteSearch = (id) => {
    if (window.confirm('Are you sure you want to delete this search record?')) {
      deleteSearch({ variables: { id } });
    }
  };

  if (loading) return <p className="text-gray-600 text-center">Loading...</p>;
  if (error) return <p className="text-red-600 text-center">Error: {error.message}</p>;

  const searches = data?.searchHistory || [];
  const userRole = propUserRole || userData?.me?.role; // Use prop if provided, otherwise get from query
  const canDelete = isMasterUser(auth.user) || userRole === 'superuser';

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg max-w-2xl mx-auto mt-6">
      <h2 className="text-2xl font-semibold text-gray-800 mb-4">Search History</h2>
      {deleteError && (
        <p className="text-red-600 text-center mb-4">Delete Error: {deleteError.message}</p>
      )}
      {searches.length === 0 ? (
        <p className="text-gray-600 text-center">No search records found.</p>
      ) : (
        <ul className="space-y-4">
          {searches.map((search) => (
            <li key={search.id} className="border p-4 rounded-md">
              <p><strong className="text-gray-700">Full Name:</strong> {search.fullName || 'N/A'}</p>
              <p><strong className="text-gray-700">ID Number:</strong> {search.idNumber || 'N/A'}</p>
              <p><strong className="text-gray-700">Entity Name:</strong> {search.entityName || 'N/A'}</p>
              <p><strong className="text-gray-700">TFS Match:</strong> <span className={search.tfsMatch ? 'text-red-600' : 'text-green-600'}>{search.tfsMatch ? 'Yes' : 'No'}</span></p>
              <p><strong className="text-gray-700">Timestamp:</strong> {search.timestamp || 'N/A'}</p>
              {search.googleError && (
                <p className="text-red-600 mt-1"><strong>Error:</strong> {search.googleError}</p>
              )}
              {search.googleResults && search.googleResults.length > 0 && (
                <div className="mt-1">
                  <strong className="text-gray-700">Google Results:</strong>
                  <ul className="list-disc list-inside ml-4">
                    {search.googleResults.map((result, index) => (
                      <li key={index} className="text-blue-600 underline">
                        <a href={result.split(': ')[1]} target="_blank" rel="noopener noreferrer">
                          {result}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="mt-2 flex space-x-2">
                <button
                  onClick={() => handleDownloadPDF(search.id)}
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition duration-200"
                >
                  Download PDF
                </button>
                {canDelete && (
                  <button
                    onClick={() => handleDeleteSearch(search.id)}
                    disabled={deleteLoading}
                    className={`px-4 py-2 rounded text-white transition duration-200 ${
                      deleteLoading ? 'bg-red-300 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600'
                    }`}
                  >
                    {deleteLoading ? 'Deleting...' : 'Delete'}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SearchHistory;